import { useMemo, useEffect } from 'react';
import { User } from 'lucide-react';

import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';

import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

function filterValidConsultants(consultants: { id: string }[], selected?: string[]): string[] {
  if (!selected) {
    return [];
  }
  const validIds = new Set(consultants.map(({ id }) => id));
  return selected.filter((id) => validIds.has(id));
}

export default function ConsultantFilter() {
  const { updateFilters, filters, filtersLoaded } = useFiltersContext();
  const hasSitesOrRegionsFilter =
    (filters.sites !== undefined && filters.sites.length > 0) ||
    (filters.regions !== undefined && filters.regions.length > 0);

  const { data: allConsultants } = trpcTenant.users.allUsersAtAvailableSiteLocations.useQuery(
    undefined,
    { enabled: !hasSitesOrRegionsFilter },
  );
  const { data: consultantsInLocations } =
    trpcTenant.locations.getConsultantsInSiteLocation.useQuery(
      {
        siteLocationIds: filters.sites,
        siteRegionIds: filters.regions,
      },
      {
        enabled: hasSitesOrRegionsFilter,
      },
    );

  const consultants = useMemo(() => {
    return (hasSitesOrRegionsFilter ? consultantsInLocations : allConsultants) ?? [];
  }, [hasSitesOrRegionsFilter, consultantsInLocations, allConsultants]);

  const options = useMemo(() => {
    return consultants
      .map((consultant) => ({
        id: consultant.id,
        value: consultant.id,
        label: `${consultant.first_name} ${consultant.last_name}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [consultants]);

  const selected = useMemo(() => {
    return filterValidConsultants(consultants, filters.consultants);
  }, [consultants, filters.consultants]);

  // When sites or regions change, we need to update the consultants filter
  // to only include consultants that are in the selected sites or regions
  useEffect(() => {
    if (consultants.length > 0 && hasSitesOrRegionsFilter && filtersLoaded) {
      const filteredConsultants = filterValidConsultants(consultants, filters.consultants);
      const currentConsultants = filters.consultants || [];

      // Only call updateFilters if the filtered consultants are actually different
      if (
        JSON.stringify(currentConsultants.sort()) !== JSON.stringify(filteredConsultants.sort())
      ) {
        updateFilters({ consultants: filteredConsultants });
      }
    }
  }, [filters.consultants, consultants, updateFilters, hasSitesOrRegionsFilter, filtersLoaded]);

  return (
    <MultipleChoiceFilter
      items={options}
      label="Consultant"
      name="consultants"
      onFilterChange={({ consultants }) => updateFilters({ consultants })}
      onlyUpdateOnChange
      value={selected.join(',')}
      variant="ghost"
      leftIcon={<User size="16px" />}
      rightIcon={undefined}
    />
  );
}
